/**
 * Typed fetch wrapper for the FilAr Panel API.
 *
 * Phase 1 scope: the wrapper itself, with refresh-on-401 hook. The domain
 * methods (auth.ts, etc.) are implemented per user story.
 */

export type ApiErrorBody = {
  error: string;
  message: string;
  detail?: Record<string, unknown> | null;
};

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly detail?: Record<string, unknown> | null;

  constructor(status: number, body: ApiErrorBody) {
    super(body.message ?? `HTTP ${status}`);
    this.status = status;
    this.code = body.error ?? "unknown_error";
    this.detail = body.detail;
  }
}

export type TokenProvider = {
  getAccessToken: () => string | null;
  /** Called once on 401; returns true if a refresh succeeded and the request should be retried. */
  refresh: () => Promise<boolean>;
};

export type ApiClientOptions = {
  baseUrl?: string;
  tokens?: TokenProvider;
  /** Defaults to fetch. Injected for tests. */
  fetcher?: typeof fetch;
};

export type RequestOptions = {
  query?: Record<string, string | number | boolean | undefined>;
  body?: unknown;
  signal?: AbortSignal;
  /** Skip the bearer token even if available (used by login endpoints). */
  anonymous?: boolean;
  /**
   * Headers extra. Se usa para `Idempotency-Key`: sin ella, un timeout de red
   * seguido de un re-tap creaba un carrete DUPLICADO — el backend ya soportaba
   * el header, el front nunca lo mandaba (auditoría 2026-08-01).
   */
  headers?: Record<string, string>;
};

export class ApiClient {
  private readonly baseUrl: string;
  private readonly tokens: TokenProvider | undefined;
  private readonly fetcher: typeof fetch;

  constructor(opts: ApiClientOptions = {}) {
    this.baseUrl = opts.baseUrl ?? "";
    this.tokens = opts.tokens;
    this.fetcher = opts.fetcher ?? fetch.bind(globalThis);
  }

  get<T>(path: string, opts: RequestOptions = {}): Promise<T> {
    return this.request<T>("GET", path, opts);
  }

  post<T>(path: string, opts: RequestOptions = {}): Promise<T> {
    return this.request<T>("POST", path, opts);
  }

  patch<T>(path: string, opts: RequestOptions = {}): Promise<T> {
    return this.request<T>("PATCH", path, opts);
  }

  delete<T>(path: string, opts: RequestOptions = {}): Promise<T> {
    return this.request<T>("DELETE", path, opts);
  }

  private async request<T>(method: string, path: string, opts: RequestOptions): Promise<T> {
    const url = this.buildUrl(path, opts.query);
    const init = this.buildInit(method, opts);

    let res = await this.fetcher(url, init);

    if (res.status === 401 && this.tokens && !opts.anonymous) {
      const refreshed = await this.tokens.refresh();
      if (refreshed) {
        res = await this.fetcher(url, this.buildInit(method, opts));
      }
    }

    if (!res.ok) {
      const body = await this.readErrorBody(res);
      throw new ApiError(res.status, body);
    }

    if (res.status === 204) {
      return undefined as T;
    }

    return (await res.json()) as T;
  }

  private buildUrl(path: string, query?: RequestOptions["query"]): string {
    const base = this.baseUrl.replace(/\/+$/, "");
    const p = path.startsWith("/") ? path : `/${path}`;
    if (!query) return `${base}${p}`;
    const qs = new URLSearchParams();
    for (const [k, v] of Object.entries(query)) {
      if (v === undefined) continue;
      qs.set(k, String(v));
    }
    const tail = qs.toString();
    return tail ? `${base}${p}?${tail}` : `${base}${p}`;
  }

  private buildInit(method: string, opts: RequestOptions): RequestInit {
    const headers: Record<string, string> = {
      Accept: "application/json",
    };
    if (opts.body !== undefined) {
      headers["Content-Type"] = "application/json";
    }
    if (this.tokens && !opts.anonymous) {
      const token = this.tokens.getAccessToken();
      if (token) headers.Authorization = `Bearer ${token}`;
    }
    if (opts.headers) Object.assign(headers, opts.headers);
    return {
      method,
      credentials: "include",
      headers,
      body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
      // UXP-1: sin timeout un request colgado deja el botón girando para
      // siempre. Si el caller pasa su propio signal lo respetamos; si no,
      // ponemos un timeout de 15s por default. humanError() ya traduce el
      // AbortError a "La conexión tardó demasiado. Reintentá.".
      signal: opts.signal ?? AbortSignal.timeout(15000),
    };
  }

  private async readErrorBody(res: Response): Promise<ApiErrorBody> {
    try {
      const parsed = (await res.json()) as Partial<ApiErrorBody>;
      return {
        error: parsed.error ?? "unknown_error",
        message: parsed.message ?? res.statusText ?? `HTTP ${res.status}`,
        detail: parsed.detail ?? null,
      };
    } catch {
      return {
        error: "unknown_error",
        message: res.statusText || `HTTP ${res.status}`,
        detail: null,
      };
    }
  }
}
